import { View } from "ui/atoms/view";
import { Button, ButtonText } from "ui/gluestack/button";
import { MedicationItemBanner } from "../molecules/MedicationItemBanner";
import { Text } from "react-native";
import { RenderInfoItem } from "ui/molecules/common/RenderInfoItem";
import { Avatar } from "ui/gluestack/avatar";
import { useMedicationAdministration } from "../hooks/useMedicationAdministration";
import StatusIndicator from "ui/atoms/avatar/statusIndicator/StatusIndicator";
import { StatusIndicatorMapping } from "app/services/api/types/residents/enums/ResidentStatusEnum";
import { MedicationModalProvider } from "../contexts/MedicationModalContext";

interface Props {
  medicationId: number;
  onClose: () => void;
  selfAdminister?: boolean;
  skipped?: boolean;
  children: React.ReactNode;
}

export default function MedicationModalTemplate({
  medicationId,
  onClose,
  selfAdminister,
  skipped,
  children,
}: Props) {
  const {
    medicationItem,
    isSubmitting,
    residentInfo,
    administerMedication,
    control,
  } = useMedicationAdministration(medicationId, {
    onAdministerSuccess: onClose,
    selfAdminister,
    skipped,
  });

  const getActionLabel = () => {
    if (skipped) return "Log";
    if (selfAdminister) return "Self Administer";
    return "Administer";
  };

  return (
    <View>
      <View className="grid md:grid-cols-2 md:gap-x-8 gap-y-4 md:gap-y-0">
        {medicationItem ? (
          <MedicationItemBanner medication={medicationItem} />
        ) : null}

        <View className="px-7 py-5 rounded-xl border border-border-200 flex md:flex-row gap-x-4 flex-1 md:items-center">
          <Avatar
            image={residentInfo.profile}
            profile={residentInfo}
            size="md"
          />
          <View className="flex flex-col flex-1 gap-y-2 mt-5 md:mt-0">
            <Text className="font-semibold overflow-hidden">
              {residentInfo.name}
              {residentInfo.nickname ? ` (${residentInfo.nickname})` : null}
            </Text>

            <View className="flex flex-wrap flex-row gap-x-2 items-center">
              {RenderInfoItem(residentInfo.roomNumber, false)}
              {RenderInfoItem(residentInfo.bedNumber, false)}
              {RenderInfoItem(`Admitted - ${residentInfo.admittedDate}`, true)}
            </View>

            {residentInfo.status ? (
              <StatusIndicator
                status={StatusIndicatorMapping[residentInfo.status]}
                statusTitle={residentInfo.status}
              />
            ) : null}
          </View>
        </View>
      </View>

      <View className="border border-gray-200 w-full my-8" />

      <MedicationModalProvider
        value={{ medication: medicationItem, control, isSubmitting }}
      >
        {children}
      </MedicationModalProvider>

      <View className="flex flex-row justify-end gap-x-4 mt-10 pb-4">
        <Button action="primary" size="sm" variant="outline" onPress={onClose}>
          <ButtonText>Cancel</ButtonText>
        </Button>
        <Button
          action="primary"
          size="sm"
          onPress={administerMedication}
          disabled={isSubmitting}
        >
          <ButtonText className="text-white">{getActionLabel()}</ButtonText>
        </Button>
      </View>
    </View>
  );
}
